import { FC, useEffect, useState } from 'react'
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
} from 'react-share'
import cx from 'classnames'

import Icon, { Icons } from '../Icon'
import { ActionButtonVariant } from '../ActionButton/styled'
import useMediaQuery from '../../hooks/useMediaQuery'

import * as SC from './styled'

// Instagram has no web share URL: the link is copied, then pasted by the user in a DM
const INSTAGRAM_DIRECT_URL = 'https://www.instagram.com/direct/inbox/'

export type AdsShareSocialNetworksProps = {
  className?: string
  title: string
  ctaLabel: string
  linkCopiedLabel: string
  urlToShare: string
  titleSharedPage: string
}

const AdsShareSocialNetworks: FC<AdsShareSocialNetworksProps> = ({
  className,
  title,
  ctaLabel,
  linkCopiedLabel,
  urlToShare,
  titleSharedPage,
}) => {
  const matches = useMediaQuery('(min-width: 768px)')
  const [isLinkCopied, setIsLinkCopied] = useState(false)
  // matches stays null until mounted, so navigator is only read client-side
  const canShareNatively = matches === false && 'share' in navigator

  useEffect(() => {
    if (!isLinkCopied) return
    const timeout = setTimeout(() => setIsLinkCopied(false), 2000)
    return () => clearTimeout(timeout)
  }, [isLinkCopied])

  const copyLink = () =>
    navigator.clipboard
      ?.writeText(urlToShare)
      .then(() => setIsLinkCopied(true))
      .catch(() => undefined)

  const shareNatively = () =>
    navigator
      .share({ title: titleSharedPage, url: urlToShare })
      .catch(() => undefined)

  return (
    <SC.AdsShareSocialNetworks
      className={cx('AdsShareSocialNetworks', className)}
    >
      {matches && <SC.Separator />}
      <SC.Wrapper>
        {canShareNatively ? (
          <SC.ShareButton
            label={ctaLabel}
            variant={ActionButtonVariant.secondary}
            iconProps={{ icon: Icons.share, width: 20, height: 20 }}
            onClick={shareNatively}
          />
        ) : (
          <>
            <SC.Title>{title}</SC.Title>
            <SC.WrapperIcons>
              <TwitterShareButton url={urlToShare} title={titleSharedPage}>
                <Icon icon={Icons.shareSocialTwitter} width={47} height={47} />
              </TwitterShareButton>

              <FacebookShareButton url={urlToShare}>
                <Icon icon={Icons.shareSocialFacebook} width={47} height={47} />
              </FacebookShareButton>

              <LinkedinShareButton url={urlToShare}>
                <Icon icon={Icons.shareSocialLinkedin} width={47} height={47} />
              </LinkedinShareButton>

              <SC.InstagramLink
                href={INSTAGRAM_DIRECT_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="instagram"
                onClick={copyLink}
              >
                <Icon
                  icon={Icons.shareSocialInstagram}
                  width={47}
                  height={47}
                />
              </SC.InstagramLink>
            </SC.WrapperIcons>
            <SC.LinkCopied role="status">
              {isLinkCopied && linkCopiedLabel}
            </SC.LinkCopied>
          </>
        )}
      </SC.Wrapper>
      <SC.Separator />
    </SC.AdsShareSocialNetworks>
  )
}

export default AdsShareSocialNetworks
